import { type DocNode } from '@atlaskit/adf-schema';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

/**
 * Checks whether a top-level adf block has a given localId anywhere in its subtree.
 */
const containsLocalId = (node: ADFEntity, localId: string): boolean => {
	if (node.attrs?.localId === localId) return true;
	return ((node.content as ADFEntity[]) ?? []).some((child) => containsLocalId(child, localId));
};

const EXTENSION_NODE_TYPES = new Set([
	'extension',
	'bodiedExtension',
	'inlineExtension',
	'multiBodiedExtension',
]);

const getTopLevelNodeAnatomy = (node: ADFEntity): string | undefined => {
	if (!EXTENSION_NODE_TYPES.has(node.type)) {
		return node.type;
	}

	const extensionKey = node.attrs?.extensionKey;
	if (typeof extensionKey !== 'string' || !extensionKey) {
		return node.type;
	}

	const extensionType = node.attrs?.extensionType;
	const isMacro = typeof extensionType === 'string' && extensionType.includes('macro');

	return `${isMacro ? 'macro' : 'extension'}:${extensionKey}`;
};

/**
 * Returns the unique top-level block anatomy labels that contain a selection.
 * Uses fullAdf + localIds because the editor gives you the contents of a selection, not its container.
 * example: selecting a table gives you its inner paragraphs, not the table itself.
 */
export const getTopLevelNodeTypes = (
	fullAdf: DocNode,
	startLocalId: string,
	endLocalId: string,
	options: {
		includeNode?: (node: ADFEntity) => boolean;
	} = {},
): string[] => {
	if (!startLocalId || !endLocalId) return [];

	try {
		const topLevelNodes = (fullAdf?.content ?? []) as ADFEntity[];

		// A selection anchor can be anywhere in the doc tree, not just at the top level. We need to know which top-level ADF component the selection belongs to.
		let startNodeIndex: number | undefined;
		let endNodeIndex: number | undefined;

		for (let i = 0; i < topLevelNodes.length; i++) {
			if (startNodeIndex === undefined && containsLocalId(topLevelNodes[i], startLocalId))
				startNodeIndex = i;
			if (endNodeIndex === undefined && containsLocalId(topLevelNodes[i], endLocalId))
				endNodeIndex = i;

			if (startNodeIndex !== undefined && endNodeIndex !== undefined) break;
		}
		if (startNodeIndex === undefined || endNodeIndex === undefined) return [];

		// The editor gives IDs in selection direction (can be right-to-left), so we use
		// min/max to get everything between the two anchors regardless of order.
		const from = Math.min(startNodeIndex, endNodeIndex);
		const to = Math.max(startNodeIndex, endNodeIndex);

		return [
			...new Set(
				topLevelNodes
					.slice(from, to + 1)
					.filter(options.includeNode ?? (() => true))
					.map(getTopLevelNodeAnatomy)
					.filter((nodeAnatomy): nodeAnatomy is string => Boolean(nodeAnatomy)),
			),
		];
	} catch {
		// swallow the error and return nothing since this is just for analytics.
		return [];
	}
};
